// New prices slide onto the end of the chart instead of the line jumping (same file in the TetherChand app).
import { useEffect, useRef, useState } from 'react';

interface TimedPoint {
    timestamp: number;
    value: number;
}

const DURATION = 600; // ms

/**
 * Returns `points`, animated: when the same chart gets newer data (a live tick, a refresh), each known point
 * eases to its new value and each new point grows out of the previous last point. A different chart
 * (`resetKey` changed, e.g. another range or currency) or older data is shown at once.
 */
export function useTweenedPoints<P extends TimedPoint>(points: P[], resetKey: string): P[] {
    const [shown, setShown] = useState(points);
    const shownRef = useRef(points);
    const keyRef = useRef(resetKey);

    useEffect(() => {
        const from = shownRef.current;
        const snap = () => {
            shownRef.current = points;
            setShown(points);
        };

        const sameChart = keyRef.current === resetKey;
        keyRef.current = resetKey;
        const prevLast = from[from.length - 1];
        const nextLast = points[points.length - 1];
        const reduced =
            typeof window !== 'undefined' &&
            window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        if (
            !sameChart ||
            reduced ||
            from.length < 2 ||
            points.length < 2 ||
            !prevLast ||
            !nextLast ||
            nextLast.timestamp < prevLast.timestamp ||
            typeof requestAnimationFrame === 'undefined'
        ) {
            snap();
            return;
        }

        // Start of each point: its old value if it was shown, else the old last point (grows from the end)
        const old = new Map(from.map((p) => [p.timestamp, p.value]));
        const start = points.map((p) =>
            old.has(p.timestamp)
                ? { timestamp: p.timestamp, value: old.get(p.timestamp)! }
                : p.timestamp > prevLast.timestamp
                  ? { timestamp: prevLast.timestamp, value: prevLast.value }
                  : { timestamp: p.timestamp, value: p.value },
        );

        let raf = 0;
        const t0 = performance.now();
        const step = (now: number) => {
            const k = Math.min(1, (now - t0) / DURATION);
            const e = 1 - Math.pow(1 - k, 3); // ease-out
            const frame = points.map((p, i) => ({
                ...p,
                timestamp: start[i].timestamp + (p.timestamp - start[i].timestamp) * e,
                value: start[i].value + (p.value - start[i].value) * e,
            }));
            shownRef.current = k < 1 ? frame : points;
            setShown(shownRef.current);
            if (k < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => {
            cancelAnimationFrame(raf);
            // An interrupted animation continues from where it stopped
        };
    }, [points, resetKey]);

    // Another chart: show its points right away, not the previous chart's for a frame
    return keyRef.current === resetKey ? shown : points;
}
